import { useFormikSubmit } from "@certn/api";
import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import { showToast } from "@certn/cascadia";
import {
  Container,
  Divider,
  InputButton,
  Link,
  LinkButton,
  PickerFormikField,
  ProgressLoader,
  Radio,
  RadioGroupFormikField,
  SectionMessage,
  Stack,
  Text,
  ToggleField,
} from "@certn/cascadia-react";
import { FormLabel } from "@certn/cascadia-react/components/inputs/_assets/input-wrapper/FormLabel";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { mixed, object } from "yup";

import { PackageInfo } from "../../PackageInfo";
import { WORKDAY_DEPLOYMENT_GUIDE_URL } from "../../constants";
import { WorkdayIntegrationContext } from "../../WorkdayIntegrationContext";
import { WhatsThis } from "../../../components/WhatsThis";
import { WorkdayIntegrationStepLayout } from "../WorkdayIntegrationStepLayout";

export function PackagesAndSettingsStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { atsIntegrationData, workdayConfig } = useContext(
    WorkdayIntegrationContext
  );

  const validationSchema = object().shape({
    packages: mixed()
      .required()
      .label(t("Packages"))
      .test((value) => value instanceof Set && value.size > 0),
  });
  const { data: packages, isLoading } = useClientPortalAPI(
    "/api/internal/client-portal/ats-integrations/packages/"
  );

  const { submit } = useFormikSubmit(
    "/api/internal/client-portal/ats-integrations/create/",
    {
      onSuccess: () => {
        showToast(t("Integration set up successfully"), {
          type: "success",
        });
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    }
  );

  return (
    <Formik
      initialValues={atsIntegrationData}
      onSubmit={(values, helpers) =>
        submit(
          {
            is_active: true,
            report_url_type: values.report_url_type,
            workday_integration: {
              oauth_token_endpoint: values.oauth_token_endpoint,
              wsdl_endpoint: values.wsdl_endpoint,
              oauth_client_id: values.oauth_client_id,
              oauth_client_secret: values.oauth_client_secret,
              oauth_refresh_token: values.oauth_refresh_token,
              oauth_refresh_token_expiry: values.oauth_refresh_token_expiry,
              oauth_refresh_token_lifetime: values.oauth_refresh_token_lifetime,

              incoming_client_id: workdayConfig?.client_id,
              incoming_client_secret: workdayConfig?.client_secret,
              incoming_access_token: workdayConfig?.access_token,
              incoming_refresh_token: workdayConfig?.refresh_token,

              should_create_users_just_in_time:
                values.should_create_users_just_in_time,

              packages: Array.from(values.packages),
            },
          },
          helpers
        )
      }
      validationSchema={validationSchema}
      validateOnMount
    >
      {({ handleSubmit, isSubmitting, isValid, setFieldValue, values }) => {
        return (
          <WorkdayIntegrationStepLayout
            onComplete={handleSubmit}
            disableCompletion={!isValid}
            submitting={isSubmitting}
          >
            <Container
              layout="narrow"
              testID="lever-details-page-outermost-container"
              className={styles.container}
            >
              <Stack gap="space-2">
                <div>
                  <Text style="input/section-title">
                    {t("step {{stepNumber}} of {{stepCount}}", {
                      stepNumber: 3,
                      stepCount: 3,
                    })}
                  </Text>
                </div>
                <Text
                  as="h1"
                  className={styles.titleSpacing}
                  style="global/title-small"
                >
                  {t("{{integration}} integration", { integration: "Workday" })}
                </Text>
                <ProgressLoader floating value={100} />
              </Stack>
              <Stack gap="space-4">
                <SectionMessage type="success" icon="verified_user">
                  {t(
                    "Workday has successfully authenticated with your account."
                  )}
                </SectionMessage>

                <SectionMessage type="info" icon="info">
                  <Trans>
                    For detailed instructions on setting up your Workday
                    integration, see our Workday{" "}
                    <Link
                      textColor="text-weak"
                      testID="deployment-guide"
                      href={WORKDAY_DEPLOYMENT_GUIDE_URL}
                      isNewTab={true}
                    >
                      Deployment Guide
                    </Link>
                    .
                  </Trans>
                </SectionMessage>

                <Text style="global/heading-medium">
                  {t(
                    "Next, select the packages you want to configure for your Workday integration."
                  )}
                </Text>
              </Stack>
              <div className={styles.mainContentContainer}>
                <section>
                  <FormLabel
                    isRequired
                    info={
                      <WhatsThis>
                        {t(
                          "Select appropriate certn package(s) to be mapped with Workday's Background check packages"
                        )}
                      </WhatsThis>
                    }
                  >
                    {t("Packages")}
                  </FormLabel>
                  <PickerFormikField
                    items={packages}
                    label={t("Packages")}
                    name="packages"
                    isLoading={isLoading}
                    selectionMode="multiple"
                    testID="packages-picker"
                    trigger={(props) => (
                      <InputButton
                        rightIcon="expand_more"
                        onToggle={props.onToggle}
                      >
                        <Text
                          style="input/text-field"
                          color={
                            props.selectedItems.length < 1
                              ? "text-placeholder"
                              : "text-base"
                          }
                        >
                          {props.selectedItems.length > 1
                            ? t("{{count}} packages selected", {
                                count: props.selectedItems.length,
                              })
                            : (props.selectedItems[0] as any)?.name ||
                              t("Please select...")}
                        </Text>
                      </InputButton>
                    )}
                  />
                </section>
                <LinkButton
                  onPress={() => window.open("/packages/create/")}
                  testID="lever-create-package-link"
                >
                  {t("Create a new package")}
                </LinkButton>{" "}
                <Divider />
                {(packages || [])
                  .filter(({ id }) =>
                    Array.from(values?.packages || []).includes(id)
                  )
                  .map((packageData, idx) => (
                    <PackageInfo
                      key={packageData.id}
                      packageData={packageData}
                      expanded={idx === 0}
                    />
                  ))}
                <Text style="global/heading-medium">
                  {t("Advanced settings")}
                </Text>
                <section>
                  <RadioGroupFormikField
                    label={t("Report URL type")}
                    testID="report-url-type"
                    name="report_url_type"
                    orientation="horizontal"
                    isRequired
                    defaultValue="DIRECT_PDF_URL"
                  >
                    <Radio testID="direct-pdf-url" value="DIRECT_PDF_URL">
                      {t("PDF URL")}
                    </Radio>
                    <Radio testID="client-portal-url" value="CLIENT_PORTAL_URL">
                      {t("Client Portal URL (Authenticated)")}
                    </Radio>
                  </RadioGroupFormikField>
                </section>
                <section>
                  <FormLabel
                    isRequired
                    info={
                      <WhatsThis>
                        {t(
                          "If enabled, a background check will be added to our system even when the user is not active. In such cases, we will automatically create the user with the minimum role needed to submit the check."
                        )}
                      </WhatsThis>
                    }
                  >
                    {t("User creation")}
                  </FormLabel>
                  <ToggleField
                    label={t("User creation")}
                    name="should_create_users_just_in_time"
                    testID="should_create_users_just_in_time"
                    aria-label="should_create_users_just_in_time"
                  >
                    {t("Create users just in time")}
                  </ToggleField>
                </section>
              </div>
            </Container>
          </WorkdayIntegrationStepLayout>
        );
      }}
    </Formik>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
  }),
  container: css({
    rowGap: "1rem !important",
  }),
  textFieldSpacing: css({
    width: "440px !important",
  }),
};
